import type { PortfolioBreakdownHierarchyNode } from 'venn-components';
import { mergeInvestmentsByLabel } from './mergeInvestmentsByLabel';
import { recalculateWeights } from './recalculateWeights';
import { isEmpty } from 'lodash';

/**
 * Filters a portfolio breakdown hierarchy to keep only the specified levels.
 * Level 0 is the root level, level 1 is the first level of children, etc.
 *
 * @param hierarchy - The root nodes of the hierarchy
 * @param levels - Array of level numbers to keep (e.g., [0, 2] keeps root and grandchildren)
 * @param options - Configuration options
 * @param options.keepFunds - Whether to keep fund nodes regardless of level
 * @param options.mergeSameLabelItems - Whether to merge nodes with the same label at each level
 * @returns Filtered hierarchy with only the specified levels
 */
export const selectLevelsFromHierarchy = (
  hierarchy: PortfolioBreakdownHierarchyNode[],
  levels: number[],
  { keepFunds, mergeSameLabelItems }: { keepFunds?: boolean; mergeSameLabelItems?: boolean } = {},
): PortfolioBreakdownHierarchyNode[] => {
  if (!levels.length && !keepFunds) {
    return [];
  }

  const minLevel = levels.length ? Math.min(...levels) : -1;

  function processNodeWithFlattening(
    node: PortfolioBreakdownHierarchyNode,
    currentLevel: number,
  ): PortfolioBreakdownHierarchyNode[] {
    const shouldKeepThisLevel = node.isFund ? keepFunds && currentLevel > minLevel : levels.includes(currentLevel);

    if (shouldKeepThisLevel) {
      // Keep this node and process its children recursively
      // Children weights remain unchanged as they're correctly proportioned to this parent
      const children = node.children?.flatMap((child) => processNodeWithFlattening(child, currentLevel + 1));
      return [{ ...node, children: !isEmpty(children) ? children : undefined }];
    }

    // Skip this level and flatten its children up to the next kept ancestor
    // Weight adjustment: multiply by skipped node's weight to maintain correct proportions
    // Example: if skipped node has weight 0.5 and child has weight 0.6,
    // the flattened child should have weight 0.3 (0.6 * 0.5) relative to the kept ancestor
    const allChildren: PortfolioBreakdownHierarchyNode[] = [];
    if (node.children?.length) {
      node.children.forEach((child) => {
        allChildren.push(...processNodeWithFlattening(child, currentLevel + 1));
      });
    }

    return allChildren;
  }

  const result: PortfolioBreakdownHierarchyNode[] = [];
  hierarchy.forEach((node) => {
    result.push(...processNodeWithFlattening(node, 0));
  });

  return mergeSameLabelItems ? mergeInvestmentsByLabel(result) : recalculateWeights(result);
};
